import React, { useState, useEffect, useContext } from "react";
import { IoIosAddCircleOutline } from "react-icons/io";
import { useNavigate, useLocation } from "react-router-dom";
import AlertContext from "../../../../Context/Alert/AlertContext";
import axios from "axios";
import { BaseURL } from "../../../../Files/BaseURL";
import ThemeContext from "../../../../Context/theme/ThemeContext";

const CreateAdd = () => {
  const { theme } = useContext(ThemeContext);
  const { showAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    price: "",
    category: "",
    country: "",
    city: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [fileURLs, setFileURLs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const location = useLocation();

  // Add file type and size constants
  const ALLOWED_FILE_TYPES = {
    image: ["image/jpeg", "image/png", "image/jpg", "image/gif"],
    video: ["video/mp4", "video/mpeg", "video/quicktime"],
    audio: ["audio/mpeg", "audio/wav", "audio/mp3"],
    pdf: ["application/pdf"],
    doc: [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${BaseURL}/api/product/productcategory`
      );
      setCategories(response.data);
    } catch (error) {
      showAlert("Error fetching categories", "danger");
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${BaseURL}/api/product/getcountry`);
      setCountries(response.data.Country); // Assuming response.data.Country is an array of countries
    } catch (error) {
      showAlert("Error fetching countries", "danger");
    }
  };

  const fetchProduct = async (id) => {
    try {
      const response = await axios.get(
        `${BaseURL}/api/product/getproduct/${id}`
      );
      const product = response.data;
      setFormData({
        title: product.Title,
        price: product.Price,
        category: product.Category._id,
        country: product.Country,
        city: product.City,
        description: product.Description,
      });
      setFileURLs(product.Images.map((url) => url.img));
    } catch (error) {
      // showAlert("Error fetching product details", "danger");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCountries();

    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get("Editid");
    if (editId) {
      fetchProduct(editId);
    }
  }, [location.search]);

  // Validation functions
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = "Please enter a valid price";
    }

    if (!formData.category) {
      newErrors.category = "Please select a category";
    }

    if (!formData.country) {
      newErrors.country = "Please select a country";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (files.length === 0) {
      newErrors.files = "Please upload at least one file";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price") {
      // Only allow positive numbers
      if (value < 0) {
        showAlert("Price cannot be negative", "danger");
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      showAlert("File size must be less than 5MB", "danger");
      return;
    }

    // Check if file type is allowed
    const isAllowedType = Object.values(ALLOWED_FILE_TYPES).some((types) =>
      types.includes(file.type)
    );

    if (!isAllowedType) {
      showAlert(
        "Please upload only image, video, audio, PDF or DOC files",
        "danger"
      );
      return;
    }

    // Check total files limit
    if (files.length >= 5) {
      showAlert("Maximum 5 files allowed", "danger");
      return;
    }

    // Add file to state
    setFiles((prev) => [...prev, file]);

    // Create preview
    if (file.type.startsWith("image/")) {
      setFileURLs((prev) => [...prev, URL.createObjectURL(file)]);
    } else {
      setFileURLs((prev) => [...prev, getFileTypeIcon(file.type)]);
    }

    // Clear file input
    e.target.value = "";
  };

  // Helper function to get file type icon/placeholder
  const getFileTypeIcon = (fileType) => {
    if (fileType.startsWith("video/")) return "/video-icon.png";
    if (fileType.startsWith("audio/")) return "/audio-icon.png";
    if (fileType.startsWith("application/pdf")) return "/pdf-icon.png";
    if (fileType.includes("document")) return "/doc-icon.png";
    return "/file-icon.png";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      showAlert("Please fill all required fields", "danger");
      return;
    }

    if (files.length === 0) {
      showAlert("Please upload at least one file", "danger");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      // Append text data
      formDataToSend.append("Title", formData.title);
      formDataToSend.append("Price", formData.price);
      formDataToSend.append("Category", formData.category);
      formDataToSend.append("Country", formData.country);
      formDataToSend.append("City", formData.city);
      formDataToSend.append("Description", formData.description);

      // Append each file
      for (let i = 0; i < files.length; i++) {
        formDataToSend.append("images", files[i]);
      }

      const authToken = localStorage.getItem("p@token");
      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      const config = {
        headers: {
          "auth-token": authToken,
          "Content-Type": "multipart/form-data",
        },
      };

      const response = await axios.post(
        `${BaseURL}/api/product/create`,
        formDataToSend,
        config
      );

      if (response.data && response.data._id) {
        showAlert("Ad created successfully", "success");
        navigate(`/user-dashboard/adds/addOverview/${response.data._id}`);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating ad:", error);
      let errorMessage = "Error creating ad. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showAlert(errorMessage, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this function to handle image upload and cropping
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      showAlert("Only JPEG, JPG, and PNG files are allowed.", "danger");
      event.target.value = "";
      return;
    }

    if (file.size < 50000) {
      // 50KB
      showAlert("File size is too small. Minimum file size is 50KB", "danger");
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Get the smallest dimension for square crop
        const size = Math.min(img.width, img.height);
        const x = (img.width - size) / 2;
        const y = (img.height - size) / 2;

        // Set canvas size to desired dimensions (800x800)
        canvas.width = 800;
        canvas.height = 800;

        // Draw cropped and resized image
        ctx.drawImage(img, x, y, size, size, 0, 0, 800, 800);

        // Convert to blob with compression
        const compressImage = async (quality = 0.7) => {
          return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), file.type, quality);
          });
        };

        let quality = 0.7;
        let blob = await compressImage(quality);

        // Compress until file size is under 5MB
        while (blob.size > 5000000 && quality > 0.1) {
          quality -= 0.1;
          blob = await compressImage(quality);
        }

        if (blob.size > 5000000) {
          showAlert(
            "Unable to compress image to under 5MB. Please try a different image.",
            "danger"
          );
          setLoading(false);
          return;
        }

        // Create form data and upload
        const formData = new FormData();
        formData.append("file", blob, file.name);

        try {
          const response = await axios.post(
            `${BaseURL}/api/upload/file`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                "auth-token": localStorage.getItem("p@token"),
              },
            }
          );

          if (response.data.success) {
            // Add image to your product images array
            setProductData((prev) => ({
              ...prev,
              Images: [...prev.Images, { img: response.data.url }],
            }));
          }
        } catch (error) {
          showAlert("Error uploading image", "danger");
        } finally {
          setLoading(false);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center font-pop flex-col">
      <div
        className={`rounded-2xl py-6 px-6 my-6 w-[95%] md:w-[70%] xl:w-[40%] flex flex-col gap-4
        ${theme === "dark" ? "bg-black text-white" : "bg-white"}`}
      >
        <h1 className="font-semibold text-xl text-center">Create Ad</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Form fields with error messages */}
          <div className="flex flex-col gap-1">
            <input
              type="text"
              name="title"
              placeholder="Ad title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-6 py-2 rounded-full ${
                errors.title
                  ? "border-2 border-red-500"
                  : "border-2 border-transparent"
              } ${theme === "dark" ? "bg-black text-white" : "bg-[#E8E8E8]"}`}
            />
            {errors.title && (
              <p className="text-red-500 text-sm ml-2">{errors.title}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <input
              type="text"
              name="price"
              placeholder="Price"
              value={formData.price}
              onChange={handleChange}
              className={`w-full px-6 py-2 rounded-full ${
                errors.price
                  ? "border-2 border-red-500"
                  : "border-2 border-transparent"
              } ${theme === "dark" ? "bg-black text-white" : "bg-[#E8E8E8]"}`}
            />
            {errors.price && (
              <p className="text-red-500 text-sm ml-2">{errors.price}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`w-full px-6 py-2 rounded-full ${
                errors.category
                  ? "border-2 border-red-500"
                  : "border-2 border-transparent"
              } ${theme === "dark" ? "bg-black text-white" : "bg-[#E8E8E8]"}`}
            >
              <option value="">Select Category</option>
              {categories.map((category, index) => (
                <option key={index} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-500 text-sm ml-2">{errors.category}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={`w-full px-6 py-2 rounded-full ${
                errors.country
                  ? "border-2 border-red-500"
                  : "border-2 border-transparent"
              } ${theme === "dark" ? "bg-black text-white" : "bg-[#E8E8E8]"}`}
            >
              <option value="">Select Country</option>
              {countries.map((country, index) => (
                <option key={index} value={country}>
                  {country}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="text-red-500 text-sm ml-2">{errors.country}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <input
              type="text"
              name="city"
              placeholder="City"
              value={formData.city}
              onChange={handleChange}
              className={`w-full px-6 py-2 rounded-full ${
                errors.city
                  ? "border-2 border-red-500"
                  : "border-2 border-transparent"
              } ${theme === "dark" ? "bg-black text-white" : "bg-[#E8E8E8]"}`}
            />
            {errors.city && (
              <p className="text-red-500 text-sm ml-2">{errors.city}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <textarea
              name="description"
              placeholder="Ad description"
              cols="30"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              className={`w-full px-6 py-2 rounded-2xl ${
                errors.description
                  ? "border-2 border-red-500"
                  : "border-2 border-transparent"
              } ${theme === "dark" ? "bg-black text-white" : "bg-[#E8E8E8]"}`}
            ></textarea>
            {errors.description && (
              <p className="text-red-500 text-sm ml-2">{errors.description}</p>
            )}
          </div>
          <div className="flex items-center justify-center h-24 w-full">
            <label
              htmlFor="dropzone-file"
              className="flex flex-col h-full items-center justify-center w-full border-2 border-primarygreen border-dashed rounded-lg cursor-pointer bg-transparent"
            >
              <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                <IoIosAddCircleOutline className="text-2xl" />
                <p className={`text-base ${theme == "dark" && "text-white"}`}>
                  Attach files (Max 5MB: Images, Videos, Audio, PDF, DOC)
                </p>
              </div>
              <input
                id="dropzone-file"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.gif,.mp4,.mp3,.wav,.pdf,.doc,.docx"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {files.map((file, index) => (
              <div key={index} className="relative">
                {file.type.startsWith("image/") ? (
                  <img
                    src={fileURLs[index]}
                    alt={`file-${index}`}
                    className="h-20 w-20 object-cover rounded-lg"
                  />
                ) : (
                  <div className="h-20 w-20 flex flex-col items-center justify-center bg-gray-100 rounded-lg p-2">
                    <img
                      src={fileURLs[index]}
                      alt={`file-${index}`}
                      className="h-10 w-10 object-contain"
                    />
                    <span className="text-xs text-center mt-1 truncate w-full">
                      {file.name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setFiles(files.filter((_, i) => i !== index));
                    setFileURLs(fileURLs.filter((_, i) => i !== index));
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`my-4 px-6 py-3 bg-green-500 font-medium text-white rounded-full
              hover:bg-transparent hover:text-black border border-black-500 hover:border-green-500
              ${theme === "dark" ? "hover:text-white" : ""} 
              ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Creating...
              </div>
            ) : (
              "Create Ad"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAdd;
